import { getToken } from './auth';

export interface GuestSlotExportRow {
  slotIndex: number | null;
  isComplete: boolean | null;
  firstName: string | null;
  surname: string | null;
  dateOfBirth: string | null;
  phone: string | null;
  address: string | null;
  country: string | null;
  documentType: string | null;
  documentNumber: string | null;
  documentCountry: string | null;
  purposeOfStay: string | null;
  visaNumber: string | null;
  hasDisabilityCard: boolean | null;
  signatureSvg: string | null;
}

export interface ReservationExportRow {
  reservationId: number;
  bookingName: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number | null;
  status: string | null;
  ubyportStatus: string | null;
  notes: string | null;
  slots: GuestSlotExportRow[];
}

export interface UbyportLookup {
  resolveCountry(code: string | null | undefined): string;
  resolvePurpose(code: string | null | undefined): string;
}

interface StateEntry {
  kod2?: string;
  kod3?: string;
  textCZ?: string;
  textENG?: string;
  translationCZ?: string;
  translationEN?: string;
}

interface PurposeEntry {
  kod2?: string;
  textCZ?: string;
  textENG?: string;
  translationCZ?: string;
  translationEN?: string;
}

export async function fetchUbyportLookup(): Promise<UbyportLookup> {
  const headers = { Authorization: `Bearer ${getToken()}` };
  const [statesRes, purposesRes] = await Promise.all([
    fetch('/api/ubyport/states', { headers }),
    fetch('/api/ubyport/purposes', { headers }),
  ]);

  const states: StateEntry[] = statesRes.ok ? await statesRes.json() : [];
  const purposes: PurposeEntry[] = purposesRes.ok ? await purposesRes.json() : [];

  const stateMap = new Map<string, StateEntry>();
  for (const s of states) {
    if (s.kod2) stateMap.set(s.kod2, s);
    if (s.kod3) stateMap.set(s.kod3, s);
    if (s.kod2 && s.kod3) stateMap.set(`${s.kod2}_${s.kod3}`, s);
    if (s.kod3 && s.kod2) stateMap.set(`${s.kod3}_${s.kod2}`, s);
  }

  const purposeMap = new Map<string, PurposeEntry>();
  for (const p of purposes) {
    if (p.kod2) purposeMap.set(p.kod2, p);
  }

  return {
    resolveCountry: (code) => {
      if (!code) return '—';
      const e = stateMap.get(code);
      if (!e) return code;
      return e.translationEN || e.textENG || e.translationCZ || e.textCZ || code;
    },
    resolvePurpose: (code) => {
      if (!code) return '—';
      const e = purposeMap.get(code);
      if (!e) return code;
      return e.translationEN || e.textENG || e.translationCZ || e.textCZ || code;
    },
  };
}

export async function fetchExportData(
  apartmentId: number,
  from: string,
  to: string
): Promise<ReservationExportRow[]> {
  const res = await fetch(
    `/api/apartments/${apartmentId}/export/data?from=${from}&to=${to}`,
    { headers: { Authorization: `Bearer ${getToken()}` } }
  );
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.json();
}

export async function downloadCsv(apartmentId: number, from: string, to: string): Promise<void> {
  const url = `/api/apartments/${apartmentId}/export/csv?from=${from}&to=${to}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(`CSV export failed: ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.setAttribute('download', `reservations-${apartmentId}-${from}-${to}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}
